/**
 * Asked before a draft is cut into a version.
 *
 * A version is immutable and permanent, so the one chance to say why it exists
 * is here. `change_note` is the only field a version carries beyond its number
 * and who published it, so it doubles as the version's name — it is what the
 * version picker shows beside `v5`.
 *
 * The number it is about to take is in the title and nowhere else. The service
 * assigns it, `PublishProcedure` takes only `change_note`, and an input for it
 * would be a box that either does nothing or disagrees with the result.
 */

import { Input, Modal } from 'antd';
import React, { FC, useState } from 'react';
import OWButton from '../ow_button/OWButton';

interface AOPPublishModalProps {
	open: boolean;
	version: number | null;
	isPublishing: boolean;
	/**
	 * Whether the draft this would cut a version from is still being written.
	 *
	 * Publishing is a read of whatever the service holds, so starting it while
	 * the last edit is still on its way can cut the version from the draft
	 * before that edit — and the edit then lands as an unpublished change on a
	 * version that reports success. The button waits rather than being hidden:
	 * a control that answers by doing nothing is the thing this screen avoids.
	 */
	isSavingDraft?: boolean;
	onCancel: () => void;
	onPublish: (changeNote: string | null) => void;
}

export const AOPPublishModal: FC<AOPPublishModalProps> = ({
	open,
	version,
	isPublishing,
	isSavingDraft,
	onCancel,
	onPublish,
}) => {
	const [note, setNote] = useState('');

	return (
		<Modal
			visible={open}
			title={version === null ? 'Publish draft as v1' : `Publish draft as v${version + 1}`}
			onCancel={onCancel}
			footer={[
				<OWButton key="cancel" onClick={onCancel}>
					Cancel
				</OWButton>,
				<OWButton
					key="publish"
					type="primary"
					loading={isPublishing || Boolean(isSavingDraft)}
					onClick={() => onPublish(note.trim() ? note.trim() : null)}
				>
					Publish
				</OWButton>,
			]}
		>
			<div className="flex flex-col gap-2xs">
				<span className="bodyregular text-character-primary">Notes</span>
				<Input.TextArea
					aria-label="Notes"
					rows={4}
					maxLength={2000}
					placeholder="What changed in this version"
					value={note}
					onChange={(event) => setNote(event.target.value)}
				/>
				{/* Said rather than only shown as a spinner, so the wait has a reason. */}
				{isSavingDraft ? (
					<span className="footnoteregular text-character-secondary">
						Saving your last edit — publishing as soon as it lands, so the version carries it.
					</span>
				) : null}
			</div>
		</Modal>
	);
};

export default AOPPublishModal;
